/**
 * context/AuthContext.js — Phase 3  (drop-in replacement for Phase 2)
 *
 * Compatibility with Phase 2:
 *  ✓ authApi.login / authApi.refresh already return data.data (unwrapped in hrmsApi.js)
 *  ✓ tokenStore API surface unchanged (setAccess, setRefresh, clearAll)
 *  ✓ All existing context values preserved (user, login, logout, loginError, loading,
 *    getAccessibleTabs, isAdmin, isSuperUser)
 *
 * New in Phase 3:
 *  + tokenStore.setExpiry() called after login & rehydration
 *  + sessionExpired state — set when user is redirected back with ?reason=session_expired
 *    (optional; used by LoginPage to show a banner if you wire it in)
 */
import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { authApi } from '../services/hrmsApi';
import { tokenStore } from '../services/api';
import IdleWarningDialog from '../components/IdleWarningDialog';

const IDLE_MS = 3 * 60 * 1000;   // 5 minutes of inactivity before logout
const WARN_MS = 30 * 1000;        // show warning 30 s before logout

export const ROLE_ACCESS = {
  'ROLE_SUPER_USER':         ['workforce-pulse', 'finance-budget', 'operations', 'talent-performance'],
  'ROLE_ADMIN':              [],
  'ROLE_WORKFORCE_PULSE':    ['workforce-pulse'],
  'ROLE_FINANCE_BUDGET':     ['finance-budget'],
  'ROLE_OPERATIONS':         ['operations'],
  'ROLE_TALENT_PERFORMANCE': ['talent-performance'],
};

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user,           setUser]           = useState(null);
  const [loginError,     setLoginError]     = useState('');
  const [loading,        setLoading]        = useState(true);
  // Phase 3 addition — optional session-expiry banner on LoginPage
  const [sessionExpired, setSessionExpired] = useState(false);

  // Idle auto-logout state
  const [showIdleWarning, setShowIdleWarning] = useState(false);
  const [idleCountdown,   setIdleCountdown]   = useState(30);
  const warnTimerRef    = useRef(null);
  const logoutTimerRef  = useRef(null);
  const countdownRef    = useRef(null);
  const warningActiveRef = useRef(false);  // true while warning dialog is visible

  // Detect redirect from silent-refresh hard failure
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('reason') === 'session_expired') {
      setSessionExpired(true);
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []);

  // Re-hydrate on page refresh using stored refresh token
  useEffect(() => {
    const rehydrate = async () => {
      const refreshToken = tokenStore.getRefresh();
      if (!refreshToken) { setLoading(false); return; }
      try {
        // authApi.refresh already unwraps data.data (see hrmsApi.js line 35)
        const authResp = await authApi.refresh(refreshToken);
        tokenStore.setAccess(authResp.accessToken);
        tokenStore.setRefresh(authResp.refreshToken);
        // Phase 3: store expiry so proactive refresh works
        tokenStore.setExpiry(Date.now() + (authResp.expiresIn ?? 900) * 1_000);
        setUser(authResp.user);
      } catch {
        tokenStore.clearAll();
      } finally {
        setLoading(false);
      }
    };
    rehydrate();
  }, []);

  const login = useCallback(async (username, password) => {
    try {
      // authApi.login already unwraps data.data (see hrmsApi.js line 25)
      const authResp = await authApi.login({ username, password });
      tokenStore.setAccess(authResp.accessToken);
      tokenStore.setRefresh(authResp.refreshToken);
      // Phase 3: store expiry so proactive refresh works
      tokenStore.setExpiry(Date.now() + (authResp.expiresIn ?? 900) * 1_000);
      setUser(authResp.user);
      setLoginError('');
      setSessionExpired(false);
      return { success: true, user: authResp.user };
    } catch (err) {
      // ACCOUNT_LOCKED is a proposed error `code` field (chunk 7 of the User
      // Access Control expansion) — every other error handler in this app
      // only reads `.message`, so this is intentionally the first place a
      // structured error code is consulted.
      const code = err.response?.data?.code;
      // attemptsRemaining is chunk 7a (proposed, not yet delivered by BE —
      // see ../hrms-contracts/changelog/frontend.md) — reading it
      // defensively here is a no-op until the backend sends it.
      const attemptsRemaining = err.response?.data?.data?.attemptsRemaining;
      let msg;
      if (code === 'ACCOUNT_LOCKED') {
        msg = 'Your account has been locked due to repeated failed login attempts. Contact an administrator to unlock it.';
      } else {
        const base = err.response?.data?.message || 'Invalid username or password.';
        msg = typeof attemptsRemaining === 'number'
          ? `${base} ${attemptsRemaining} attempt${attemptsRemaining === 1 ? '' : 's'} remaining before your account is locked.`
          : base;
      }
      setLoginError(msg);
      return { success: false };
    }
  }, []);

  const logout = useCallback(async () => {
    try { await authApi.logout(); } catch { /* ignore — clear locally regardless */ }
    finally { tokenStore.clearAll(); setUser(null); }
  }, []);

  // Called by activity events — ignored while warning dialog is visible
  const resetIdleTimer = useCallback(() => {
    if (warningActiveRef.current) return;

    clearTimeout(warnTimerRef.current);
    clearTimeout(logoutTimerRef.current);
    clearInterval(countdownRef.current);

    warnTimerRef.current = setTimeout(() => {
      warningActiveRef.current = true;
      setShowIdleWarning(true);
      setIdleCountdown(30);
      countdownRef.current = setInterval(() =>
        setIdleCountdown(prev => prev - 1), 1000);
    }, IDLE_MS - WARN_MS);

    logoutTimerRef.current = setTimeout(() => logout(), IDLE_MS);
  }, [logout]);

  // Called only by the "Stay Logged In" button — always resets regardless of warning state
  const handleStayLoggedIn = useCallback(() => {
    warningActiveRef.current = false;
    clearTimeout(warnTimerRef.current);
    clearTimeout(logoutTimerRef.current);
    clearInterval(countdownRef.current);
    setShowIdleWarning(false);
    setIdleCountdown(30);

    warnTimerRef.current = setTimeout(() => {
      warningActiveRef.current = true;
      setShowIdleWarning(true);
      setIdleCountdown(30);
      countdownRef.current = setInterval(() =>
        setIdleCountdown(prev => prev - 1), 1000);
    }, IDLE_MS - WARN_MS);

    logoutTimerRef.current = setTimeout(() => logout(), IDLE_MS);
  }, [logout]);

  useEffect(() => {
    if (!user) {
      clearTimeout(warnTimerRef.current);
      clearTimeout(logoutTimerRef.current);
      clearInterval(countdownRef.current);
      warningActiveRef.current = false;
      setShowIdleWarning(false);
      return;
    }
    resetIdleTimer();
    const EVENTS = ['mousemove', 'mousedown', 'keydown', 'touchstart', 'scroll', 'click'];
    EVENTS.forEach(e => window.addEventListener(e, resetIdleTimer, { passive: true }));
    return () => {
      EVENTS.forEach(e => window.removeEventListener(e, resetIdleTimer));
      clearTimeout(warnTimerRef.current);
      clearTimeout(logoutTimerRef.current);
      clearInterval(countdownRef.current);
    };
  }, [user, resetIdleTimer]);

  const getAccessibleTabs = useCallback(() => {
    if (!user) return [];
    const tabs = new Set();
    (user.roles || []).forEach((role) => {
      (ROLE_ACCESS[role] || []).forEach((tab) => tabs.add(tab));
    });
    return Array.from(tabs);
  }, [user]);

  const isAdmin     = useCallback(() => user?.roles?.includes('ROLE_ADMIN')      || false, [user]);
  const isSuperUser = useCallback(() => user?.roles?.includes('ROLE_SUPER_USER') || false, [user]);

  // Called by ForceChangePasswordDialog after a successful forced password
  // change, so ProtectedRoute stops blocking without requiring a re-login.
  const clearMustChangePassword = useCallback(() => {
    setUser((u) => (u ? { ...u, mustChangePassword: false } : u));
  }, []);

  return (
    <AuthContext.Provider value={{
      user, login, logout,
      loginError, setLoginError,
      loading,
      sessionExpired, setSessionExpired,   // Phase 3 addition
      getAccessibleTabs, isAdmin, isSuperUser,
      clearMustChangePassword,
    }}>
      {children}
      <IdleWarningDialog
        open={showIdleWarning}
        countdown={idleCountdown}
        onStayLoggedIn={handleStayLoggedIn}
      />
    </AuthContext.Provider>
  );
}

export function useAuth() { return useContext(AuthContext); }
